var BigNumber	 		= require("./type/BigNumber"),
	BooleanType 		= require("./type/BooleanType"),
	CalendarTime 		= require("./type/CalendarTime"),
	CalendarDate 		= require("./type/CalendarDate"),
	CalendarDateTime	= require("./type/CalendarDateTime"),
	Cell				= require("../data/Cell"),
	Currency 			= require("./type/Currency"),
	Customizing 		= require("./Customizing"),
	Enumeration 		= require("./type/Enumeration"),
	Numeric 			= require("./type/Numeric"),
	Text		 		= require("./type/Text"),
	Timezone		 	= require("./type/Timezone");

/**
 * The Column class represents the field type of columns. It handles
 * styling, data types and data binding.
 *
 * @param {(Object|string)} oData Data object representing the Column configuration
 * @param {string} oData.property References the data object property that is bound as value for this column
 * @param {string} [oData.textAlign] Optional alignment
 * @param {string} [oData.label] Optional column label that will be shown in the header row
 * @param {string} [oData.type] Optional data type for this column. Default: Column.Type.Text
 * @param {string} [oData.width] Optional width of the column
 * @param {SharedStrings} oSharedStrings SharedStrings instance that will be used by all Cells
 * @param {Styles} oStyles Styles collection that is needed to register each Style a Column uses
 * @class
 * @module binding/Column
 */
function Column(oData, oSharedStrings, oStyles) {
	var oColumnData;

	/* Fallback assignment if only a key is provided */
	oColumnData = typeof oData === "string" ? {property: oData} : oData;
	if (!oColumnData || (typeof oColumnData.property !== "string" && !Array.isArray(oColumnData.property))) {
		throw new Error("The property for the column is not defined");
	}

	this.property = oColumnData.property;
	this.label = oColumnData.label ? oColumnData.label : oColumnData.property;
	this.width = oColumnData.width;
	this.styles = oStyles;
	this.type = this._initType(oColumnData);
	this.cell = new Cell(this.type, oSharedStrings);
}

/**
 * Initializes the data type for this Column. Type specific
 * formatting information will be evaluated and applied.
 *
 * @param {Object} oData Configuration object for this Column that also contains type and formatting information
 * @param {string} oData.type Specifies the data type
 * @param {boolean} [oData.autoScale] Specifies if the scale customizing is being used
 * @param {string} [oData.calendar] Specifies the calendar if type is date, datetime or time
 * @param {boolean} [oData.delimiter] Specifies if a thousands delimiter is shown
 * @param {boolean} [oData.displayTimezone] Specifies if the IANA timezone will be visible in the cell
 * @param {boolean} [oData.displayUnit] Specifies if the UoM will be shown or not if type is number or currency
 * @param {string} [oData.falseValue] Specifies the displayed value if the boolean type has the value false
 * @param {string} [oData.format] Specifies a custom format if type is date, datetime or time
 * @param {string} [oData.hierarchyLevel] Specifies the name of the property that contains the hierarchy level
 * @param {string} [oData.inputFormat] Specifies the input format of a string based date
 * @param {number} [oData.scale] Specifies the decimal scale
 * @param {string} [oData.template] Specifies a template string that can be filled with several properties
 * @param {string} [oData.textAlign] Defines the horizontal alignment
 * @param {string} [oData.timezone] Specific timezone that will be applied to the Column
 * @param {string} [oData.timezoneProperty] Property name that references the timezone
 * @param {string} [oData.trueValue] Specifies the displayed value if the boolean type has the value true
 * @param {string} [oData.unit] Specifies the UoM if type is number
 * @param {string} [oData.unitProperty] Name of the property that contains the UoM if type is number or currency
 * @param {Object} [oData.valueMap] Contains a JS object or Map with key value pairs
 *
 * @returns {Type} - Returns the specific Type object that contains all the formatting
 * @private
 */
// eslint-disable-next-line complexity
Column.prototype._initType = function(oData) {
	var oType, sType;

	sType = typeof oData.type === "string" ? oData.type.toLowerCase() : oData.type;

	switch (sType) {
		case "datetime":

			/* Add translated timezone to column header of type datetime if fixed timezone is used */
			if (oData.timezone && typeof oData.timezone === "string" && !oData.timezoneProperty) {
				this.label += ` (${Customizing.getTimezoneTranslation(oData.timezone)})`;
			}
			oType = new CalendarDateTime(oData);
			break;
		case "date":
			oType = new CalendarDate(oData);
			break;
		case "time":
			oType = new CalendarTime(oData);
			break;
		case "timezone":
			oType = new Timezone(oData);
			break;
		case "percentage":
		case "number":
			oType = new Numeric(oData);
			break;
		case "currency":
			oType = new Currency(oData);
			break;
		case "boolean":
			oType = new BooleanType(oData);
			break;
		case "enumeration":
			oType = new Enumeration(oData);
			break;
		case "bignumber":
			oType = new BigNumber(oData);
			break;
		default:
			oData.type = "text";

			oType = new Text(oData);
	}

	oType.registerAt(this.styles);

	return oType;
};

/**
 * Returns the Cell object that is referenced by this particular
 * Column. Every Column references only one single Cell that gets
 * reused for each and every Row.
 *
 * When binding the actual data to a row, the Cell content, its
 * references and its styling get updated and a serialized version
 * of the row is stored within the sheet.
 *
 * @returns {Cell} - The Cell that belongs to this particular Column
 */
Column.prototype.getCell = function() {
	return this.cell;
};

/**
 * This method binds the actual business object property to the columns cell.
 *
 * @param {object} oData - Business object that gets bound to the Cell of this Column.
 */
Column.prototype.bind = function(oData) {

	/*
	 * Due to the fact that Cell#setValue updates the Style depending
	 * on the actual Cell value (i.E. Unit of Measurement for
	 * Currencies), we need to provide the original business object
	 * so that the Cell knows the whole business object instead of
	 * just the value of its particular property.
	 */
	try {
		this.cell.setValue(this.property, oData);
	} catch (oError) {

		// Enhance error message with column and line item information
		oError.message = (oError.message ? oError.message + "\n\n" : "") + "Column: " + this.label;
		oError.message += "\nData: " + JSON.stringify(oData);

		throw oError;
	}
};

module.exports = Column;